import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, FormControl, TextField } from '@mui/material';
import { debounce } from 'lodash';

import APIService from '../../../services/APIService';
import { type AdminUser } from '../../../types/types';

interface UserPickerProps {
  onUserSelect: (user_id: string) => void;
  minSearchLength?: number; // Minimum characters before searching
}

const UserPicker: React.FC<UserPickerProps> = ({ onUserSelect, minSearchLength = 2 }) => {
  const [availableUsers, setAvailableUsers] = useState<AdminUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Track if we've done an initial search
  const hasSearched = useRef(false);

  const fetchUsers = useCallback(async (search: string = '') => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    setLoading(true);

    const queryParams = new URLSearchParams({
      page: '1',
      pageSize: '50', // Reduced from 1000 to 50 for better performance
      sortBy: '',
      sortOrder: '',
      searchQuery: search,
      featureFilter: '',
      cohortFilter: '',
    });

    try {
      const response = await APIService.makeAPIGetRequest({
        requestString: `/user/getUsers?${queryParams}`,
        accessToken: accessToken,
      });

      if (response.ok) {
        setAvailableUsers(response.value.users);
        hasSearched.current = true;
      } else {
        console.error('Failed to fetch users:', response.error);
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setAvailableUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a debounced version of fetchUsers
  const debouncedFetchUsers = useRef(
    debounce((search: string) => {
      fetchUsers(search);
    }, 300),
  ).current;

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [debouncedFetchUsers]);

  const handleInputChange = (_event: React.ChangeEvent<{}>, value: string, reason: string) => {
    setInputValue(value);

    // Only trigger search when user is actually typing
    if (reason === 'input') {
      // Cancel any pending debounced calls
      debouncedFetchUsers.cancel();

      if (value.trim().length >= minSearchLength) {
        debouncedFetchUsers(value.trim());
      } else if (value.trim().length === 0) {
        // Clear results when input is empty
        setAvailableUsers([]);
        hasSearched.current = false;
      }
    }
  };

  const handleUserChange = (_event: any, value: AdminUser | null) => {
    setSelectedUser(value);
    if (value) {
      onUserSelect(value._id);
    } else {
      onUserSelect('');
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // Fetch initial set of users when dropdown opens if we haven't searched yet
    if (!hasSearched.current && inputValue.length < minSearchLength) {
      fetchUsers('');
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box>
      <FormControl fullWidth>
        <Autocomplete
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          options={availableUsers}
          loading={loading}
          value={selectedUser}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleUserChange}
          getOptionLabel={option => (option.user_info?.name ? option.user_info.name : option._id)}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          renderInput={params => (
            <TextField
              {...params}
              label="Choose a user"
              variant="outlined"
              placeholder={
                minSearchLength > 0
                  ? `Type at least ${minSearchLength} characters to search...`
                  : 'Start typing to search...'
              }
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          noOptionsText={
            inputValue.trim().length < minSearchLength
              ? `Type at least ${minSearchLength} characters to search`
              : loading
                ? 'Searching...'
                : 'No users found'
          }
        />
      </FormControl>
    </Box>
  );
};

export default UserPicker;
